import * as React from "react";
import type { LucideIcon } from "lucide-react";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * Padrão de modal do sistema: cabeçalho azul da lotérica, corpo com respiro e
 * rodapé separado. Botões secundários usam `appDialogSecondaryButton` (o
 * variant "outline" do Button é preto neste projeto).
 */
export const appDialogSecondaryButton =
  "bg-white text-gray-800 border-gray-300 hover:bg-gray-100 dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800";

export const appDialogPrimaryButton =
  "bg-primary text-white hover:bg-primary/90";

/** Campos de formulário claros dentro do modal (select/botões de busca). */
export const appDialogField =
  "bg-white text-gray-900 border-gray-300 hover:bg-gray-50 dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800";

type AppDialogContentProps = React.ComponentPropsWithoutRef<typeof DialogContent> & {
  title: string;
  description?: string;
  icon?: LucideIcon;
};

export const AppDialogContent = ({
  title,
  description,
  icon: Icon,
  className,
  children,
  ...props
}: AppDialogContentProps) => (
  <DialogContent
    className={cn(
      "sm:max-w-lg max-h-[92vh] overflow-y-auto p-0 sm:p-0 gap-0 [&>button]:text-white [&>button]:opacity-90 [&>button]:hover:opacity-100",
      className
    )}
    {...props}
  >
    <DialogHeader className="bg-primary text-white px-6 py-5 space-y-0 text-left">
      <div className="flex items-center gap-3 pr-6">
        {Icon && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/15">
            <Icon className="h-5 w-5" />
          </div>
        )}
        <div className="min-w-0">
          <DialogTitle className="text-white text-lg leading-tight">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-white/80 text-sm mt-0.5">
              {description}
            </DialogDescription>
          )}
        </div>
      </div>
    </DialogHeader>
    {children}
  </DialogContent>
);

export const AppDialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("grid gap-5 px-6 py-5", className)} {...props} />
);

export const AppDialogFooter = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogFooter>) => (
  <DialogFooter
    className={cn(
      "flex-row justify-end gap-2 border-t bg-zinc-100/60 dark:bg-zinc-900/40 px-6 py-4 sm:space-x-0",
      className
    )}
    {...props}
  />
);
